import { c as _c } from "react/compiler-runtime";
// Акции площадки (П3, fd-promo-32): консоль объявляет акцию — название,
// период, условия участия, скидка, — продавцы подают на неё заявки со своими
// товарами. Одобренные товары видны в составе акции. Раздел только на русском,
// как и «Промокоды и партнёры», — строки прямо в разметке.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Inbox, Package, Pencil, Plus, Power, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { api, type PlatformPromoCampaign, type PlatformPromoCampaignInput } from "@/api";
import { dateTimeRu, num, rub } from "@/components/promo-kit";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useCanManagePromos } from "./promos";
const STATE_LABEL: Record<PlatformPromoCampaign["state"], string> = {
  scheduled: "Запланирована",
  active: "Идёт",
  finished: "Завершена",
  paused: "Выключена"
};
const STATE_CLASS: Record<PlatformPromoCampaign["state"], string> = {
  scheduled: "text-amber-600",
  active: "text-emerald-600",
  finished: "text-muted-foreground",
  paused: "text-muted-foreground"
};
export const campaignDiscount = (c: Pick<PlatformPromoCampaign, "discountType" | "discountValue">) => c.discountType === "percent" ? `${c.discountValue} %` : rub(c.discountValue);

/** «ГГГГ-ММ-ДДTчч:мм» по местному времени для datetime-local. */
const toLocal = (iso: string) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};
type Draft = {
  title: string;
  description: string;
  conditions: string;
  discountType: "percent" | "amount";
  discountValue: string;
  startsAt: string;
  endsAt: string;
  accepting: boolean;
  isPublic: boolean;
};
const blank = (): Draft => ({
  title: "",
  description: "",
  conditions: "",
  discountType: "percent",
  discountValue: "15",
  startsAt: toLocal(new Date(Date.now() + 7 * 86_400_000).toISOString()),
  endsAt: toLocal(new Date(Date.now() + 10 * 86_400_000).toISOString()),
  accepting: true,
  isPublic: true
});
const fromCampaign = (c: PlatformPromoCampaign): Draft => ({
  title: c.title,
  description: c.description,
  conditions: c.conditions,
  discountType: c.discountType,
  discountValue: String(c.discountValue),
  startsAt: toLocal(c.startsAt),
  endsAt: toLocal(c.endsAt),
  accepting: c.accepting,
  isPublic: c.isPublic
});
export function PlatformPromoCampaignsPage() {
  const $ = _c(50);
  const queryClient = useQueryClient();
  const canManage = useCanManagePromos();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["platform-promo-campaigns"],
      queryFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data,
    error,
    isLoading
  } = useQuery(t0);
  const [editor, setEditor] = useState(null);
  const [composition, setComposition] = useState(null);
  const [actionError, setActionError] = useState(null);
  let t1;
  if ($[1] !== queryClient) {
    t1 = () => queryClient.invalidateQueries({
      queryKey: ["platform-promo-campaigns"]
    });
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const refresh = t1;
  let t2;
  if ($[3] !== refresh) {
    t2 = async c => {
      setActionError(null);
      ;
      try {
        await api.promoCampaigns.update(c.id, {
          enabled: !c.enabled
        });
        await refresh();
      } catch (t3) {
        const e = t3;
        setActionError((e as Error).message);
      }
    };
    $[3] = refresh;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const toggle = t2;
  let t3;
  if ($[5] !== data) {
    t3 = data ?? [];
    $[5] = data;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const list = t3;
  const shownError = actionError ?? (error ? (error as Error).message : null);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div><h1 className="text-xl font-semibold">Акции площадки</h1><p className="text-sm text-muted-foreground">Общие акции OSMIX: продавцы подают заявки со своими товарами, одобренные товары входят в акцию. Публичные видны покупателям в «Календаре акций».</p></div>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Button variant="outline" asChild={true}><Link to="/promo-applications"><Inbox className="mr-2 size-4" />Заявки продавцов</Link></Button>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== canManage) {
    t6 = canManage && <Button onClick={() => setEditor({})} data-testid="campaign-create"><Plus className="mr-2 size-4" />Новая акция</Button>;
    $[9] = canManage;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <div className="flex flex-wrap items-start justify-between gap-3">{t4}<div className="flex flex-wrap gap-2">{t5}{t6}</div></div>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== shownError) {
    t8 = shownError && <p className="text-sm text-destructive">{shownError}</p>;
    $[13] = shownError;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <TableHeader><TableRow><TableHead>Акция</TableHead><TableHead>Период</TableHead><TableHead>Скидка</TableHead><TableHead>Статус</TableHead><TableHead className="text-right">Товары</TableHead><TableHead className="text-right">Заявки</TableHead><TableHead className="w-[120px]" /></TableRow></TableHeader>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== isLoading) {
    t10 = isLoading && <TableRow><TableCell colSpan={7} className="h-20 text-center text-muted-foreground">Загрузка…</TableCell></TableRow>;
    $[16] = isLoading;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== isLoading || $[19] !== list.length) {
    t11 = !isLoading && list.length === 0 && <TableRow><TableCell colSpan={7} className="h-24 text-center text-muted-foreground">Акций площадки пока нет</TableCell></TableRow>;
    $[18] = isLoading;
    $[19] = list.length;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== canManage || $[22] !== list || $[23] !== toggle) {
    let t13;
    if ($[25] !== canManage || $[26] !== toggle) {
      t13 = c_0 => <TableRow key={c_0.id} data-testid={`campaign-row-${c_0.id}`}><TableCell><div className="font-medium">{c_0.title}</div>{c_0.conditions && <div className="text-xs text-muted-foreground">Условия: {c_0.conditions}</div>}<div className="mt-0.5 flex flex-wrap gap-2 text-xs text-muted-foreground">{c_0.isPublic ? <span>В календаре</span> : <span>Скрыта из календаря</span>}<span>{c_0.accepting ? "\u041F\u0440\u0438\u0451\u043C \u0437\u0430\u044F\u0432\u043E\u043A \u043E\u0442\u043A\u0440\u044B\u0442" : "\u041F\u0440\u0438\u0451\u043C \u0437\u0430\u044F\u0432\u043E\u043A \u0437\u0430\u043A\u0440\u044B\u0442"}</span></div></TableCell><TableCell className="whitespace-nowrap text-sm">{dateTimeRu(c_0.startsAt)}<br />{dateTimeRu(c_0.endsAt)}</TableCell><TableCell>{campaignDiscount(c_0)}</TableCell><TableCell className={cn("text-sm", STATE_CLASS[c_0.state])}>{STATE_LABEL[c_0.state]}</TableCell><TableCell className="text-right"><button type="button" className="inline-flex items-center gap-1 text-sm hover:underline" onClick={() => setComposition(c_0)}><Package className="size-4" />{num(c_0.productCount)}</button></TableCell><TableCell className="text-right"><Link to={`/promo-applications?targetId=${encodeURIComponent(c_0.id)}`} className="text-sm hover:underline">{c_0.pendingCount > 0 ? `${num(c_0.pendingCount)} ждут` : "\u2014"}</Link></TableCell><TableCell>{canManage && <div className="flex justify-end gap-1"><Button variant="ghost" size="icon" aria-label={"\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C"} onClick={() => setEditor({
              campaign: c_0
            })}><Pencil className="size-4" /></Button><Button variant="ghost" size="icon" aria-label={c_0.enabled ? "\u0412\u044B\u043A\u043B\u044E\u0447\u0438\u0442\u044C" : "\u0412\u043A\u043B\u044E\u0447\u0438\u0442\u044C"} onClick={() => void toggle(c_0)}><Power className={cn("size-4", c_0.enabled ? "text-emerald-600" : "text-muted-foreground")} /></Button></div>}</TableCell></TableRow>;
      $[25] = canManage;
      $[26] = toggle;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    t12 = list.map(t13);
    $[21] = canManage;
    $[22] = list;
    $[23] = toggle;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t12) {
    t13 = <div className="overflow-x-auto rounded-lg border"><Table className="min-w-[860px]">{t9}<TableBody>{t10}{t11}{t12}</TableBody></Table></div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const t14 = Boolean(editor);
  const t15 = editor?.campaign;
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setEditor(null);
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== refresh) {
    t17 = async () => {
      setEditor(null);
      await refresh();
    };
    $[33] = refresh;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t14 || $[36] !== t15 || $[37] !== t17) {
    t18 = <CampaignEditor open={t14} campaign={t15} onClose={t16} onSaved={t17} />;
    $[35] = t14;
    $[36] = t15;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setComposition(null);
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== canManage || $[41] !== composition || $[42] !== refresh) {
    t20 = <CompositionDialog campaign={composition} canManage={canManage} onClose={t19} onChanged={refresh} />;
    $[40] = canManage;
    $[41] = composition;
    $[42] = refresh;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t13 || $[45] !== t18 || $[46] !== t20 || $[47] !== t7 || $[48] !== t8) {
    t21 = <div className="flex flex-col gap-4" data-testid="platform-promo-campaigns">{t7}{t8}{t13}{t18}{t20}</div>;
    $[44] = t13;
    $[45] = t18;
    $[46] = t20;
    $[47] = t7;
    $[48] = t8;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  return t21;
}
function _temp() {
  return api.promoCampaigns.list();
}
function CampaignEditor({
  open,
  campaign,
  onClose,
  onSaved
}: {
  open: boolean;
  campaign?: PlatformPromoCampaign;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setDraft(campaign ? fromCampaign(campaign) : blank());
    setError(null);
  }, [open, campaign]);
  const set = <K extends keyof Draft,>(key: K, value: Draft[K]) => setDraft(d => ({
    ...d,
    [key]: value
  }));
  const submit = async () => {
    const value_0 = Number(draft.discountValue.replace(",", "."));
    if (!draft.title.trim()) return setError("Укажите название акции");
    if (!Number.isFinite(value_0) || value_0 < 0) return setError("Некорректный размер скидки");
    if (draft.discountType === "percent" && value_0 > 99) return setError("Скидка в процентах не может быть больше 99");
    if (!draft.startsAt || !draft.endsAt || new Date(draft.endsAt) <= new Date(draft.startsAt)) return setError("Окончание акции раньше её начала");
    const input: PlatformPromoCampaignInput = {
      title: draft.title.trim(),
      description: draft.description.trim(),
      conditions: draft.conditions.trim(),
      discountType: draft.discountType,
      discountValue: value_0,
      startsAt: new Date(draft.startsAt).toISOString(),
      endsAt: new Date(draft.endsAt).toISOString(),
      accepting: draft.accepting,
      isPublic: draft.isPublic
    };
    setBusy(true);
    setError(null);
    try {
      if (campaign) await api.promoCampaigns.update(campaign.id, input);else await api.promoCampaigns.create(input);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const field = "flex flex-col gap-1 text-sm";
  const selectClass = "h-9 rounded-md border border-input bg-background px-3 text-sm";
  return <Dialog open={open} onOpenChange={next => !next && onClose()}>
			<DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="campaign-editor">
				<DialogHeader>
					<DialogTitle>{campaign ? "Акция площадки" : "Новая акция площадки"}</DialogTitle>
					<DialogDescription>Продавцы увидят акцию в кабинете на вкладке «Заявки на участие».</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4">
					<label className={field}>
						<span className="text-muted-foreground">Название</span>
						<Input value={draft.title} maxLength={120} onChange={e_0 => set("title", e_0.target.value)} placeholder="Чёрная пятница OSMIX" />
					</label>
					<label className={field}>
						<span className="text-muted-foreground">Описание для покупателей</span>
						<textarea className="min-h-[64px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={draft.description} maxLength={1000} onChange={e_1 => set("description", e_1.target.value)} />
					</label>
					<label className={field}>
						<span className="text-muted-foreground">Условия участия для продавцов</span>
						<textarea className="min-h-[64px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={draft.conditions} maxLength={2000} onChange={e_2 => set("conditions", e_2.target.value)} placeholder="Скидка не меньше 15 %, товар в наличии" />
					</label>
					<div className="grid gap-3 sm:grid-cols-2">
						<label className={field}>
							<span className="text-muted-foreground">Тип скидки</span>
							<select className={selectClass} value={draft.discountType} onChange={e_3 => set("discountType", e_3.target.value as Draft["discountType"])}>
								<option value="percent">Проценты</option>
								<option value="amount">Рубли</option>
							</select>
						</label>
						<label className={field}>
							<span className="text-muted-foreground">Размер скидки</span>
							<Input inputMode="decimal" value={draft.discountValue} onChange={e_4 => set("discountValue", e_4.target.value)} />
						</label>
						<label className={field}>
							<span className="text-muted-foreground">Начало</span>
							<Input type="datetime-local" value={draft.startsAt} onChange={e_5 => set("startsAt", e_5.target.value)} />
						</label>
						<label className={field}>
							<span className="text-muted-foreground">Окончание</span>
							<Input type="datetime-local" value={draft.endsAt} onChange={e_6 => set("endsAt", e_6.target.value)} />
						</label>
					</div>
					<label className="flex items-center gap-2 text-sm">
						<input type="checkbox" checked={draft.accepting} onChange={e_7 => set("accepting", e_7.target.checked)} />
						Принимать заявки продавцов
					</label>
					<label className="flex items-center gap-2 text-sm">
						<input type="checkbox" checked={draft.isPublic} onChange={e_8 => set("isPublic", e_8.target.checked)} />
						Показывать в календаре акций
					</label>
					{error && <p className="text-sm text-destructive">{error}</p>}
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={busy}>
						Отмена
					</Button>
					<Button onClick={() => void submit()} disabled={busy} data-testid="campaign-save">
						Сохранить
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>;
}
function CompositionDialog(t0) {
  const $ = _c(42);
  const {
    campaign,
    canManage,
    onClose,
    onChanged
  } = t0;
  const queryClient = useQueryClient();
  const t1 = campaign?.id;
  let t2;
  if ($[0] !== t1) {
    t2 = ["platform-promo-campaigns", "items", t1];
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== campaign?.id) {
    t3 = () => api.promoCampaigns.items(campaign?.id ?? "");
    $[2] = campaign?.id;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = Boolean(campaign);
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    data,
    isLoading
  } = useQuery(t5);
  let t6;
  if ($[8] !== campaign || $[9] !== onChanged || $[10] !== queryClient) {
    t6 = async productId => {
      if (!campaign) {
        return;
      }
      await api.promoCampaigns.removeItem(campaign.id, productId);
      await queryClient.invalidateQueries({
        queryKey: ["platform-promo-campaigns", "items", campaign.id]
      });
      onChanged();
    };
    $[8] = campaign;
    $[9] = onChanged;
    $[10] = queryClient;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const remove = t6;
  let t7;
  if ($[12] !== data) {
    t7 = data ?? [];
    $[12] = data;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const items = t7;
  const t8 = Boolean(campaign);
  let t9;
  if ($[14] !== onClose) {
    t9 = next => !next && onClose();
    $[14] = onClose;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DialogTitle>Состав акции</DialogTitle>;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = campaign?.title;
  let t12;
  if ($[17] !== t11) {
    t12 = <DialogHeader>{t10}<DialogDescription>{t11}</DialogDescription></DialogHeader>;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== isLoading) {
    t13 = isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>;
    $[19] = isLoading;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== isLoading || $[22] !== items.length) {
    t14 = !isLoading && items.length === 0 && <p className="text-sm text-muted-foreground">Пока ни одного одобренного товара</p>;
    $[21] = isLoading;
    $[22] = items.length;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== canManage || $[25] !== items || $[26] !== remove) {
    let t16;
    if ($[28] !== canManage || $[29] !== remove) {
      t16 = item => <li key={item.productId} className="flex items-center gap-3 py-2">{item.photo ? <img src={item.photo} alt="" className="size-10 rounded object-cover" /> : <div className="size-10 rounded bg-muted" />}<div className="min-w-0 flex-1"><div className="truncate text-sm">{item.name}</div><div className="text-xs text-muted-foreground">{item.shopName} · {rub(item.price)}</div></div>{canManage && <Button variant="ghost" size="icon" aria-label={"\u0423\u0431\u0440\u0430\u0442\u044C \u0438\u0437 \u0430\u043A\u0446\u0438\u0438"} onClick={() => void remove(item.productId)}><Trash2 className="size-4" /></Button>}</li>;
      $[28] = canManage;
      $[29] = remove;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    t15 = items.map(t16);
    $[24] = canManage;
    $[25] = items;
    $[26] = remove;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[31] !== t15) {
    t16 = <ul className="flex flex-col divide-y">{t15}</ul>;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t12 || $[34] !== t13 || $[35] !== t14 || $[36] !== t16) {
    t17 = <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl">{t12}{t13}{t14}{t16}</DialogContent>;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t17 || $[39] !== t8 || $[40] !== t9) {
    t18 = <Dialog open={t8} onOpenChange={t9}>{t17}</Dialog>;
    $[38] = t17;
    $[39] = t8;
    $[40] = t9;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  return t18;
}